import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import api from "../services/api";

const TrialContext = createContext(null);

/** Hook for any component to read trial state and control the popup. */
export const useTrial = () => useContext(TrialContext);

// Derive the current trial state from the user's temporary-access expiry.
//   "expiring" → within 24h of expiry (soft warning, access still works)
//   "expired"  → past expiry (new executions blocked, history still readable)
//   null       → no temporary access, or plenty of time left
function computeScenario(user) {
  const expiresAt = user?.temporaryAccess?.expiresAt;
  if (!expiresAt) return null;
  const expiry = new Date(expiresAt).getTime();
  if (Number.isNaN(expiry)) return null;
  const now = Date.now();
  if (now >= expiry) return "expired";
  if (expiry - now <= 24 * 60 * 60 * 1000) return "expiring";
  return null;
}

export function TrialProvider({ user, children }) {
  const scenario = computeScenario(user);
  const expired = scenario === "expired";

  const [open, setOpen] = useState(false);
  // Which scenario the user has dismissed. Keyed by scenario value so that a
  // transition (e.g. "expiring" → "expired") re-opens the popup even after the
  // earlier one was dismissed in the same session.
  const [dismissedFor, setDismissedFor] = useState(null);

  // Server-sourced record of what this user has already submitted. The
  // TrialSubmission model is the source of truth, so completed forms stay
  // completed across browsers and devices.
  const [submitted, setSubmitted] = useState({
    feedback: false,
    extension: false,
    rating: null,
  });
  const [statusLoaded, setStatusLoaded] = useState(false);

  // Load submission status from the backend for any logged-in user. Feedback is
  // a shared, account-wide signal (same for the trial popup and the menu popup),
  // so it is fetched regardless of whether a trial scenario is active.
  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setStatusLoaded(true);
      return;
    }
    setStatusLoaded(false);
    api
      .getTrialStatus()
      .then((res) => {
        if (cancelled) return;
        setSubmitted({
          feedback: !!res.data.feedback,
          extension: !!res.data.extension,
          rating: res.data.rating ?? null,
        });
      })
      .catch(() => {
        /* fall back to "nothing submitted" on error */
      })
      .finally(() => {
        if (!cancelled) setStatusLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const allSubmitted = submitted.feedback && submitted.extension;

  // Auto-open when an undismissed scenario is active — but wait until status is
  // loaded, and skip if the user has already submitted both forms.
  useEffect(() => {
    if (
      scenario &&
      dismissedFor !== scenario &&
      statusLoaded &&
      !allSubmitted
    ) {
      setOpen(true);
    }
  }, [scenario, dismissedFor, statusLoaded, allSubmitted]);

  const markSubmitted = useCallback((type) => {
    setSubmitted((prev) => ({ ...prev, [type]: true }));
  }, []);

  const markRating = useCallback((value) => {
    setSubmitted((prev) => ({ ...prev, rating: value }));
  }, []);

  const dismiss = useCallback(() => {
    setOpen(false);
    setDismissedFor(scenario);
  }, [scenario]);

  const openModal = useCallback(() => setOpen(true), []);

  // Called by execution flows. If the trial has expired, blocks the action and
  // re-opens the popup; returns false. Otherwise returns true.
  const requireActive = useCallback(() => {
    if (expired) {
      setOpen(true);
      return false;
    }
    return true;
  }, [expired]);

  const value = useMemo(
    () => ({
      scenario,
      expired,
      open,
      openModal,
      dismiss,
      requireActive,
      submitted,
      markSubmitted,
      markRating,
    }),
    [
      scenario,
      expired,
      open,
      openModal,
      dismiss,
      requireActive,
      submitted,
      markSubmitted,
      markRating,
    ],
  );

  return (
    <TrialContext.Provider value={value}>{children}</TrialContext.Provider>
  );
}
